import { SaveOperation } from '@infuse/api/common';
import { AddPermissionsAction } from '@infuse/iam/role/type';
import { UiNotificationType } from '@infuse/ui/domain';
import { useNotify } from '@infuse/ui/services';
import { getAxiosErrorMessage } from '@infuse/utils';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { useI18n } from 'vue-i18n';

export const useGenericGrantPermission = ({
  apiFn,
  forcedParentId
}: {
  apiFn: SaveOperation<
    {
      permissionIds: string[];
      replace: boolean;
    },
    never
  >;
  forcedParentId?: string;
}) => {
  const queryClient = useQueryClient();
  const notify = useNotify();
  const { t } = useI18n({ useScope: 'global' });

  const mutation = useMutation<unknown, unknown, AddPermissionsAction & { parentId?: string }>({
    mutationFn: async ({ parentId, permissionIds, replace }) => {
      if (forcedParentId) {
        parentId = forcedParentId;
      }
      try {
        await apiFn({ id: parentId }, { permissionIds, replace });
        notify({
          title: t('iam.permissions.grant_permissions.header'),
          message: t('iam.permissions.grant_permissions.success')
        });
      } catch (e) {
        notify({
          type: UiNotificationType.ERROR,
          title: t('iam.permissions.grant_permissions.header'),
          message: getAxiosErrorMessage(e, t('iam.permissions.grant_permissions.failed'))
        });
        throw e;
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['permissions'] });
    }
  });

  return {
    ...mutation
  };
};
